import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ThumbsUp, ShieldCheck, Wrench, PlayCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import NewsletterForm from "@/components/NewsletterForm";
import ShareButtons from "@/components/ShareButtons";
import SuggestedKits from "@/components/SuggestedKits";

const SITE = "https://spa-report-hub.emergent.host";

function getEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
}

export default function TutorialDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tuto, setTuto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [voting, setVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/tutorials/${slug}`)
      .then((r) => {
        setTuto(r.data);
        setHasVoted(!!r.data.current_user_helpful);
      })
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useSEO(
    tuto
      ? {
          title: `${tuto.title} — Tuto communautaire | DoctoSpa`,
          description: tuto.summary,
          image: tuto.cover_photo_url ? `${backend}${tuto.cover_photo_url}` : undefined,
          url: `${SITE}/tutos/${tuto.slug}`,
          type: "article",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: tuto.title,
            description: tuto.summary,
            author: { "@type": "Person", name: tuto.author_name },
            datePublished: tuto.published_at,
          },
        }
      : { title: "Tuto — DoctoSpa" }
  );

  const onVote = async () => {
    if (!user) {
      toast.info("Connectez-vous pour voter");
      navigate("/login");
      return;
    }
    setVoting(true);
    try {
      if (hasVoted) {
        const r = await api.delete(`/tutorials/${slug}/helpful`);
        setTuto((t) => ({ ...t, helpful_count: r.data.helpful_count }));
        setHasVoted(false);
        toast.success("Vote retiré");
      } else {
        const r = await api.post(`/tutorials/${slug}/helpful`);
        setTuto((t) => ({ ...t, helpful_count: r.data.helpful_count }));
        setHasVoted(true);
        toast.success("Merci — ça aide la communauté ! 👍");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return <p className="mx-auto max-w-3xl px-6 py-16 text-sm text-muted-foreground">Chargement…</p>;
  }
  if (notFound || !tuto) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center" data-testid="tuto-not-found">
        <h1 className="font-display text-2xl font-bold">Tuto introuvable</h1>
        <p className="mt-2 text-sm text-muted-foreground">Ce tuto n'existe pas ou n'est pas encore publié.</p>
        <Link to="/tutos">
          <Button className="mt-6 rounded-full">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux tutos
          </Button>
        </Link>
      </div>
    );
  }

  const embedUrl = getEmbedUrl(tuto.video_url);
  const bodyPhotos = (tuto.photos || []).filter((p) => p.path && p.path !== tuto.cover_photo_path);

  return (
    <article className="mx-auto max-w-3xl px-6 lg:px-10 py-12 lg:py-16" data-testid="tutorial-detail-page">
      <Link to="/tutos" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="tuto-back-link">
        <ArrowLeft className="h-4 w-4" /> Tous les tutos
      </Link>

      <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
        <Badge variant="secondary" className="rounded-full capitalize">{tuto.category.replace("-", " ")}</Badge>
        {tuto.spa_brand && <Badge variant="outline" className="rounded-full">{tuto.spa_brand}</Badge>}
        {(tuto.tags || []).map((t) => (
          <Badge key={t} variant="outline" className="rounded-full text-[10px]">#{t}</Badge>
        ))}
      </div>

      <h1 className="font-display text-3xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
        {tuto.title}
      </h1>

      <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <span className="grid place-items-center h-8 w-8 rounded-full bg-primary/10 text-primary font-bold text-xs">
            {(tuto.author_name?.[0] || "U").toUpperCase()}
          </span>
          <span className="font-semibold text-foreground">{tuto.author_name}</span>
          {tuto.author_is_technician && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/10 text-secondary font-semibold text-[10px]" data-testid="author-verified-badge">
              <ShieldCheck className="h-3 w-3" /> Technicien vérifié
            </span>
          )}
        </span>
        <span className="flex items-center gap-1 text-xs">
          <Eye className="h-3.5 w-3.5" /> {tuto.views} vues
        </span>
      </div>

      <div className="mt-5">
        <ShareButtons title={tuto.title} testIdPrefix="tuto-share-top" contentType="tutorial" slug={tuto.slug} />
      </div>

      {tuto.summary && (
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground border-l-4 border-primary/40 pl-4 italic">
          {tuto.summary}
        </p>
      )}

      {tuto.cover_photo_url && (
        <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/9] bg-slate-900" data-testid="tuto-cover">
          <img src={`${backend}${tuto.cover_photo_url}`} alt={tuto.title} className="h-full w-full object-cover" />
        </div>
      )}

      {embedUrl && (
        <div className="mt-8 rounded-2xl overflow-hidden aspect-video bg-slate-900" data-testid="tuto-video">
          <iframe
            src={embedUrl}
            title={tuto.title}
            className="h-full w-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      )}
      {tuto.video_url && !embedUrl && (
        <a href={tuto.video_url} target="_blank" rel="noopener noreferrer" className="mt-8 flex items-center gap-2 text-primary hover:underline text-sm" data-testid="tuto-video-link">
          <PlayCircle className="h-5 w-5" /> Voir la vidéo → {tuto.video_url}
        </a>
      )}

      <div className="prose prose-slate max-w-none mt-10 prose-headings:font-display prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-a:text-primary" data-testid="tuto-body">
        <ReactMarkdown>{tuto.body_markdown}</ReactMarkdown>
      </div>

      {bodyPhotos.length > 0 && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="tuto-photos">
          {bodyPhotos.map((p, i) => (
            <a key={p.path || i} href={`${backend}${p.url}`} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden bg-slate-100">
              <img src={`${backend}${p.url}`} alt={p.filename || "photo"} className="h-full w-full object-cover hover:scale-105 transition-transform" loading="lazy" />
            </a>
          ))}
        </div>
      )}

      {/* Vote CTA */}
      <Card className="mt-12 p-6 border-2 border-primary/20 bg-primary/5" data-testid="helpful-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-lg">Ce tuto vous a été utile ?</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Aidez la communauté en signalant si vous l'avez testé et si ça a marché.
            </p>
          </div>
          <Button
            onClick={onVote}
            disabled={voting}
            className={`rounded-full h-12 px-6 gap-2 ${hasVoted ? "bg-secondary hover:bg-secondary/90" : "bg-primary hover:bg-primary/90"}`}
            data-testid="btn-helpful-vote"
          >
            <ThumbsUp className={`h-5 w-5 ${hasVoted ? "fill-white" : ""}`} />
            {hasVoted ? "J'ai testé, ça marche ✓" : "J'ai testé, ça marche"}
            <Badge className="ml-2 bg-white/20 hover:bg-white/20 text-white" data-testid="helpful-count-badge">
              {tuto.helpful_count}
            </Badge>
          </Button>
        </div>
      </Card>

      {/* Partage — attire du trafic */}
      <div className="mt-10 rounded-2xl border border-black/10 bg-stone-50 p-6">
        <p className="text-sm font-semibold">Ce tuto vous a aidé ? Partagez-le à un autre propriétaire de spa 🛁</p>
        <div className="mt-3">
          <ShareButtons title={tuto.title} testIdPrefix="tuto-share-bottom" contentType="tutorial" slug={tuto.slug} />
        </div>
      </div>

      {/* Kit complet lié à ce tuto */}
      <SuggestedKits
        brand={tuto.spa_brand}
        category={tuto.category === "cover" ? "other" : tuto.category}
        limit={3}
        title="Le kit complet pour suivre ce tuto"
        subtitle="Toutes les pièces de ce tutoriel, testées et livrées dans un même carton — avec notice."
      />

      {/* Bottom CTA */}
      <div className="mt-16 rounded-2xl bg-slate-900 text-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="eyebrow text-white/60">Contribuez</div>
          <h3 className="mt-2 font-display text-xl font-bold">
            Vous avez une astuce à partager ? Publiez votre propre tuto.
          </h3>
        </div>
        <Link to={user ? "/tutos/nouveau" : "/login"}>
          <Button className="bg-primary hover:bg-primary/90 rounded-full h-11 px-6" data-testid="tuto-cta-create">
            Proposer un tuto
          </Button>
        </Link>
      </div>

      {/* Newsletter */}
      <div className="mt-8">
        <NewsletterForm source="tuto" />
      </div>
    </article>
  );
}
